
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { logger } from "@/lib/logger";

/**
 * Hook that provides optimized event handlers for Slack integration
 */
export const useSlackHandlers = (
  aiStaffId: string,
  baseHandlers: {
    connectionHandleConnect: (
      webhookUrl: string,
      slackToken: string,
      saveToDatabase: boolean,
      triggerTag: string,
      isFileProcessingEnabled: boolean,
      isAutoResponsesEnabled: boolean
    ) => Promise<boolean>;
    baseOnDisconnect: () => Promise<boolean>;
  },
  state: {
    webhookUrl: string;
    slackToken: string;
    triggerTag: string;
    isFileProcessingEnabled: boolean;
    isAutoResponsesEnabled: boolean;
  }
) => {
  const { connectionHandleConnect, baseOnDisconnect } = baseHandlers;
  const { webhookUrl, slackToken, triggerTag, isFileProcessingEnabled, isAutoResponsesEnabled } = state;
  const queryClient = useQueryClient();

  // Create optimized implementation of onConnect that uses current state
  const handleConnect = useCallback(async () => {
    try {
      const result = await connectionHandleConnect(
        webhookUrl,
        slackToken,
        true,
        triggerTag,
        isFileProcessingEnabled,
        isAutoResponsesEnabled
      );
      
      if (result) {
        // Invalidate cached data to refresh UI
        queryClient.invalidateQueries({ 
          queryKey: ['slackIntegration', aiStaffId]
        });
      }
      
      return result;
    } catch (error) {
      logger.error({
        message: "Error in handleConnect",
        error: error instanceof Error ? error : new Error(String(error))
      });
      return false;
    }
  }, [
    connectionHandleConnect, 
    webhookUrl, 
    slackToken,
    triggerTag,
    isFileProcessingEnabled,
    isAutoResponsesEnabled,
    queryClient,
    aiStaffId
  ]);
  
  // Optimized disconnect handler
  const handleDisconnect = useCallback(async () => {
    const result = await baseOnDisconnect();
    
    if (result) {
      // Invalidate cached data to refresh UI
      queryClient.invalidateQueries({ 
        queryKey: ['slackIntegration', aiStaffId]
      });
    }
    
    return result;
  }, [baseOnDisconnect, queryClient, aiStaffId]);

  return {
    handleConnect,
    handleDisconnect
  };
};
